/* eslint-disable max-len */
import React, { useEffect } from "react";

import { CloseIcon } from "../../../components/Icons";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import { PREQUAL_RENEWAL_PARAM } from "../../../services/prequalification";
import { StyledBookingFlowButton } from "../../containers/BookingScheduleContainer/BookingScheduleContainer.style";
import { logPrequalEvent } from "../../containers/PrequalificationContainer/prequalAnalytics";
import { useModal } from "../../../context/ModalContext";
import { useNavigate } from "react-router-dom";

const goodVetsLogo = "/assets/icons/logo_black.svg";
const scratchPayLogo = "/assets/icons/scratchpay.png";

/**
 * Intro/interceptor screen shown when a renewing customer taps "See Your
 * Options" but has no valid prequalification yet (Figma 38597-21710).
 * "Continue" routes into the /prequalification funnel scoped to the renewal
 * pets; "Skip" (and the close icon) return to the dashboard.
 * @return {JSX.Element} The interceptor modal.
 */
const PrequalInterceptorModal = () => {
  const { closeModal, params, source } = useModal();
  const navigate = useNavigate();

  const renewalPetIds = (params?.renewalPetIds as string[] | undefined) ?? [];

  useEffect(() => {
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_INTERCEPTOR_SHOWN, {
      referred_from: source,
      number_of_pets: renewalPetIds.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = () => {
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_INTERCEPTOR_CONTINUE, {
      referred_from: source,
      number_of_pets: renewalPetIds.length,
    });
    closeModal();

    // The renewal cohort itself is derived from endsSoonWPData in the
    // container; the URL only carries a clean renewal flag (and a prefilled
    // pet id when there's a single renewal pet, so the picker is skipped).
    const searchParams = new URLSearchParams({ [PREQUAL_RENEWAL_PARAM]: "true" });
    if (renewalPetIds.length === 1) {
      searchParams.set("id", renewalPetIds[0]);
    }

    navigate(`${PAGES_PATH.PREQUALIFICATION}?${searchParams.toString()}`);
  };

  const handleSkip = () => {
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_INTERCEPTOR_SKIP, {
      referred_from: source,
      number_of_pets: renewalPetIds.length,
    });
    closeModal();
  };

  return (
    <div className="flex flex-col gap-6 w-full bg-[#F9F6F4] rounded-3xl p-8 xl:w-[30rem]">
      {/* Top nav */}
      <div className="flex items-center justify-end w-full">
        <div className="w-8 h-8 bg-stone-50 rounded-full flex items-center justify-center cursor-pointer">
          <CloseIcon onClick={handleSkip} className="text-light-black" />
        </div>
      </div>

      <div className="flex flex-col gap-6 items-center w-full mb-[90px]">
        <img src={goodVetsLogo} alt="GoodVets" className="h-10 w-auto object-contain" />

        <div className="flex gap-3 items-center">
          <span className="font-semibold text-sm leading-[14px] text-light-black">Powered by</span>
          <img src={scratchPayLogo} alt="Scratch" className="h-6 w-auto object-contain" />
        </div>

        <div className="flex flex-col gap-3 items-center text-center text-light-black w-full">
          <h3 className="font-semibold text-[30px] leading-8 grenette-pro">A New Way to Pay for TotalCare</h3>
          <p className="text-base leading-5">
            GoodVets has partnered with Scratch to offer monthly billing for our TotalCare plans. To
            show you your available billing and plan options, we need to confirm a few details.
          </p>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-2 items-center w-full">
        <StyledBookingFlowButton
          disabled={false}
          fullWidth
          color="primary"
          size="small"
          onClick={handleContinue}
        >
          Continue
        </StyledBookingFlowButton>
        <button
          type="button"
          onClick={handleSkip}
          className="w-full h-10 flex items-center justify-center text-sm font-medium text-light-black bg-transparent border-none cursor-pointer"
        >
          Skip
        </button>
      </div>
    </div>
  );
};

export default PrequalInterceptorModal;
